/*
* === SystemSettingsPage.js (Final - Enhanced with Security & Communication) ===
*/
import React, { useEffect, useState } from 'react';
import { 
    Container, Typography, Card, CardContent, Box, Alert, Switch, 
    FormControlLabel, TextField, Button, Grid, CircularProgress, Divider, 
    TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, InputAdornment,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import DeleteIcon from '@mui/icons-material/Delete';
import TimerIcon from '@mui/icons-material/Timer'; // New
import LockPersonIcon from '@mui/icons-material/LockPerson'; // New
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'; // New
import CampaignIcon from '@mui/icons-material/Campaign'; // New
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import EditIcon from '@mui/icons-material/Edit';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

// --- Sub-Component: Logo Settings (Fixed & Cleaned) ---
const LogoSettings = ({ token, showSnackbar }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const { refreshSettings, logoUrl } = useSettings();

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        
        // Increased to 5MB to match our new Google Cloud limits
        if (selectedFile.size > 5 * 1024 * 1024) { 
            showSnackbar('File too large (Max 5MB).', 'error'); 
            return; 
        }
        if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(selectedFile.type)) { 
            showSnackbar('Invalid file type.', 'error'); 
            return; 
        }
        setFile(selectedFile);
    };   
    
};

// --- Sub-Component: Tier Configuration ---
const TierSettings = ({ settings, onChange }) => {
    // 1. ADD 'incubator' to the array
    const tiers = ['incubator', 'starter', 'growth', 'enterprise'];
    
    // 2. ADD the label for Incubator
    const labels = { 
        incubator: 'Incubator (PAYG)',
        starter: 'Starter (Small)', 
        growth: 'Growth (Mid-Corp)', 
        enterprise: 'Enterprise' 
    };

    const getVal = (tier, type) => {
        const key = `tier_${tier}_max_${type}`;
        const found = settings.find(s => s.SettingKey === key);
        return found ? found.SettingValue : '';
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CardMembershipIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Subscription Tier Limits</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                    Set numeric limits. Use <strong>-1</strong> for Unlimited.
                </Alert>
                <Grid container spacing={2}>
                    {tiers.map(tier => (
                        <Grid item xs={12} key={tier}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                                {labels[tier]}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField 
                                    label="Max Users" size="small" type="number" fullWidth
                                    value={getVal(tier, 'users')}
                                    onChange={(e) => onChange(`tier_${tier}_max_users`, e.target.value)}
                                />
                                <TextField 
                                    label="Max Funds" size="small" type="number" fullWidth
                                    value={getVal(tier, 'funds')}
                                    onChange={(e) => onChange(`tier_${tier}_max_funds`, e.target.value)}
                                />
                                <TextField 
                                    label="Max Apps" size="small" type="number" fullWidth
                                    value={getVal(tier, 'apps')}
                                    onChange={(e) => onChange(`tier_${tier}_max_apps`, e.target.value)}
                                />
                            </Box>
                            <Divider sx={{ my: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

// --- Main Page ---
const SystemSettingsPage = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { token } = useAuth();
    const { refreshSettings } = useSettings();
    const [logoFile, setLogoFile] = useState(null);
    const [cronLogs, setCronLogs] = useState([]);
    
    // 🚨 ADD THESE TWO LINES:
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    // Add this to your main data fetching function (or a useEffect)
    const fetchCronLogs = async () => {
        try {
            const res = await api.get('/admin/cron-logs', { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            setCronLogs(res.data);
        } catch (error) {
            console.error("Failed to load cron logs", error);
        }
    };    

    // Load settings from API
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/admin/settings', { headers: { Authorization: `Bearer ${token}` } });
                
                // 🚨 ADD THIS BLOCK to fetch the companies for the dropdown
                try {
                    const compRes = await api.get('/admin/companies', { headers: { Authorization: `Bearer ${token}` } });
                    setCompanies(compRes.data);
                } catch (cErr) { console.error("Failed to load companies", cErr); }

                api.get('/admin/cron-logs', { headers: { Authorization: `Bearer ${token}` } })
                    .then(res => setCronLogs(res.data))
                    .catch(err => console.error("Failed to load cron logs", err));

                setSettings(response.data);
            } catch (err) { setError('Failed to load settings.'); } 
            finally { setLoading(false); }
        };
        if (token) {
            fetchCronLogs(); 
        }
        fetchSettings();
    }, [token]);

    // Find your logo upload function and update the API call to look exactly like this:
    // We pass 'uploadType' so the function knows EXACTLY what to tell the backend
    // We now pass the specific company ID as the second argument
    const handleLogoUpload = async (uploadType, selectedCompanyId = null) => {
        if (!logoFile) return;
        
        const formData = new FormData();
        formData.append('logo', logoFile);

        if (uploadType === 'system') {
            formData.append('isSystemLogo', 'true');
        } else if (uploadType === 'tenant') {
            // 🚨 THE FIX: Check the argument passed in, not a hardcoded state variable
            if (!selectedCompanyId) {
                alert("Please select a tenant to upload their logo.");
                return;
            }
            formData.append('companyId', selectedCompanyId);
        }

        try {
            await api.post('/admin/upload-logo', formData, {
                headers: { 
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}` 
                }
            });
            
            alert(`${uploadType === 'system' ? 'System' : 'Tenant'} Logo uploaded successfully!`);
            setLogoFile(null); 
            window.location.reload(); 
            
        } catch (error) {
            console.error("Upload failed", error);
            alert(`Failed to upload ${uploadType} logo.`);
        }
    };

    const handleChange = (key, value) => {
        setSettings(prev => {
            const exists = prev.find(s => s.SettingKey === key);
            if (exists) {
                const stringValue = String(value); 
                return prev.map(s => s.SettingKey === key ? { ...s, SettingValue: stringValue } : s);
            }
            return [...prev, { SettingKey: key, SettingValue: String(value), Category: 'System' }];
        });
    };

    const getValue = (key, defaultVal = 'false') => {
        const setting = settings.find(s => s.SettingKey === key);
        return setting ? setting.SettingValue : defaultVal;
    };

    const handleSave = async () => {
        setError(''); setSuccess('');
        try {
            const updates = settings.map(s => ({ key: s.SettingKey, value: s.SettingValue }));
            await api.put('/admin/settings', updates, { headers: { Authorization: `Bearer ${token}` } });
            
            setSuccess('Settings saved.');
            refreshSettings(); 
            window.scrollTo(0,0);
        } catch (err) { setError('Save failed.'); window.scrollTo(0,0); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">System Configuration</Typography>
                <Button variant="contained" size="large" startIcon={<SaveIcon />} onClick={handleSave}>Save Changes</Button>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            
            <LogoSettings token={token} showSnackbar={(msg, type) => type === 'error' ? setError(msg) : setSuccess(msg)} />

            <Grid container spacing={3}>
                {/* 1. Branding */}
                <Grid item xs={12} md={6}>
                    {/* BRANDING SECTION */}
                    <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Branding
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                            {/* NEW: Display the active logo from the database! */}
                            {settings && settings.system_logo ? (
                                <Box 
                                    component="img" 
                                    src={settings.system_logo} 
                                    alt="System Logo" 
                                    sx={{ height: 60, width: 'auto', objectFit: 'contain', border: '1px dashed #ccc', p: 1, borderRadius: 1 }}
                                />
                            ) : (
                                <Box sx={{ height: 60, width: 120, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">No Logo Found</Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
                                <Button variant="outlined" component="label" size="small" sx={{ alignSelf: 'flex-start' }}>
                                    Select Image
                                    <input 
                                        type="file" 
                                        hidden 
                                        accept="image/*" 
                                        onChange={(e) => setLogoFile(e.target.files[0])}
                                    />
                                </Button>
                                {logoFile && <Typography variant="caption" color="primary">{logoFile.name}</Typography>}
                                
                                <Divider sx={{ my: 1 }} />

                                {/* BrightSparc System Logo Button */}
                                <Button 
                                    variant="contained" 
                                    onClick={() => handleLogoUpload('system')}
                                    disabled={!logoFile}
                                >
                                    Upload BrightSparc System Logo
                                </Button>

                                {/* Tenant Logo Selection & Upload */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Select Tenant</InputLabel>
                                        <Select
                                            value={selectedCompanyId}
                                            label="Select Tenant"
                                            onChange={(e) => setSelectedCompanyId(e.target.value)}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {companies.map(c => (
                                                <MenuItem key={c.Id} value={c.Id}>{c.CompanyName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button 
                                        variant="contained" 
                                        color="secondary"
                                        onClick={() => handleLogoUpload('tenant', selectedCompanyId)}
                                        disabled={!logoFile || !selectedCompanyId}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Upload Tenant Logo
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
                
                {/* 2. Access Control (Existing) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><SecurityIcon color="warning" sx={{ mr: 1 }} /><Typography variant="h6">Access Control</Typography></Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 2, color: 'warning.contrastText' }}>
                                <FormControlLabel
                                    control={<Switch checked={getValue('coming_soon_mode') === 'true'} onChange={(e) => handleChange('coming_soon_mode', e.target.checked)} color="default" />}
                                    label={<Typography fontWeight="bold">Enable "Coming Soon" Mode</Typography>}
                                />
                                <Typography variant="caption" display="block">Hides app behind landing page. SuperUsers bypass this.</Typography>
                            </Box>
                            
                            <FormControlLabel
                                control={<Switch checked={getValue('maintenance_mode') === 'true'} onChange={(e) => handleChange('maintenance_mode', e.target.checked)} color="error" />}
                                label="Enable Maintenance Mode"
                                sx={{ mb: 1, display: 'block' }}
                            />
                            
                            <FormControlLabel
                                control={<Switch checked={getValue('allow_registrations', 'true') === 'true'} onChange={(e) => handleChange('allow_registrations', e.target.checked)} color="success" />}
                                label="Allow New Registrations"
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* NEW: Subscription Tier Configuration */}
                <Grid item xs={12} md={6}>
                    <TierSettings settings={settings} onChange={handleChange} />
                </Grid>
                
                {/* 3. General Settings (Existing) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><SettingsIcon color="action" sx={{ mr: 1 }} /><Typography variant="h6">General</Typography></Box>
                            <Divider sx={{ mb: 2 }} />
                            <TextField fullWidth label="Platform Name" value={getValue('platform_name', 'BrightSparc')} onChange={(e) => handleChange('platform_name', e.target.value)} margin="normal" />
                            <TextField fullWidth label="Support Email" value={getValue('support_email', '')} onChange={(e) => handleChange('support_email', e.target.value)} margin="normal" />
                            <TextField 
                                fullWidth 
                                type="number" 
                                label="Max Upload Size (MB)" 
                                value={getValue('max_upload_size_mb', '10')} 
                                onChange={(e) => handleChange('max_upload_size_mb', e.target.value)} 
                                margin="normal"
                                InputProps={{ endAdornment: <InputAdornment position="end">MB</InputAdornment> }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* 4. Security & Governance (NEW) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <LockPersonIcon color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6">Security & Governance</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField 
                                        fullWidth 
                                        label="Session Timeout" 
                                        type="number"
                                        value={getValue('session_timeout_mins', '30')} 
                                        onChange={(e) => handleChange('session_timeout_mins', e.target.value)} 
                                        InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField 
                                        fullWidth 
                                        label="Lockout Threshold" 
                                        type="number"
                                        helperText="Failed attempts before lock"
                                        value={getValue('max_failed_login_attempts', '5')} 
                                        onChange={(e) => handleChange('max_failed_login_attempts', e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField 
                                        fullWidth 
                                        label="Password Expiry" 
                                        type="number"
                                        helperText="Days before reset required (0 = Disabled)"
                                        value={getValue('password_expiry_days', '90')} 
                                        onChange={(e) => handleChange('password_expiry_days', e.target.value)} 
                                        InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={getValue('require_mfa_admin') === 'true'} onChange={(e) => handleChange('require_mfa_admin', e.target.checked)} color="primary" />}
                                        label="Enforce MFA for Admin Roles"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                
                {/* 5. CRONJOBS MONITOR SECTION (Wrapped in a Paper to match layout!) */}
                <Grid item xs={12} md={6}>
                   {/* --- BACKGROUND JOBS MONITOR --- */}
                    <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Background Jobs Monitor
                        </Typography>
                        
                        {/* Adding a fixed height and scrollbar keeps the UI clean even with long logs */}
                        <TableContainer variant="outlined" sx={{ maxHeight: 350, overflowY: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: 'background.paper' }}><strong>Job Name</strong></TableCell>
                                        <TableCell sx={{ bgcolor: 'background.paper' }}><strong>Status</strong></TableCell>
                                        <TableCell sx={{ bgcolor: 'background.paper' }}><strong>Executed At</strong></TableCell>
                                        <TableCell sx={{ bgcolor: 'background.paper' }}><strong>Details</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cronLogs && cronLogs.length > 0 ? (
                                        cronLogs.map((log, index) => (
                                            <TableRow key={log.Id || index} hover>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                                                    {log.JobName}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={log.Status} 
                                                        size="small" 
                                                        color={log.Status === 'Success' ? 'success' : 'error'} 
                                                        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                    {new Date(log.ExecutedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                                                    <Typography variant="body2" sx={{ 
                                                        display: '-webkit-box', 
                                                        WebkitLineClamp: 2, 
                                                        WebkitBoxOrient: 'vertical', 
                                                        overflow: 'hidden',
                                                        fontSize: '0.85rem'
                                                    }} title={log.Message || log.message || log.Details || ''}>
                                                        {/* 🚨 THE FIX: Catch all possible key variations */}
                                                        {log.Message || log.message || log.Details || 'No message provided.'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                No cron jobs have run yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* 6. Communication & Alerts (NEW) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CampaignIcon color="info" sx={{ mr: 1 }} />
                                <Typography variant="h6">Communication & Alerts</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ mb: 3 }}>
                                <FormControlLabel
                                    control={<Switch checked={getValue('system_announcement_active') === 'true'} onChange={(e) => handleChange('system_announcement_active', e.target.checked)} color="warning" />}
                                    label="Activate System-Wide Banner"
                                />
                                <TextField 
                                    fullWidth 
                                    multiline
                                    rows={2}
                                    label="Announcement Message" 
                                    placeholder="e.g., Scheduled maintenance this Friday at 22:00."
                                    value={getValue('system_announcement_banner', '')} 
                                    onChange={(e) => handleChange('system_announcement_banner', e.target.value)} 
                                    disabled={getValue('system_announcement_active') !== 'true'}
                                    sx={{ mt: 1 }}
                                />
                            </Box>

                            <TextField 
                                fullWidth 
                                label="Default Email 'From' Name" 
                                value={getValue('default_email_from_name', 'BrightSparc System')} 
                                onChange={(e) => handleChange('default_email_from_name', e.target.value)} 
                                margin="dense"
                            />
                            <TextField 
                                fullWidth 
                                label="Support Phone / WhatsApp" 
                                value={getValue('support_phone', '')} 
                                onChange={(e) => handleChange('support_phone', e.target.value)} 
                                margin="dense"
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* 7. Maintenance Details (Existing) */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><TimerIcon color="disabled" sx={{ mr: 1 }} /><Typography variant="h6">Maintenance Scheduler</Typography></Box>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField 
                                        fullWidth 
                                        type="datetime-local"
                                        label="Estimated End Time" 
                                        InputLabelProps={{ shrink: true }}
                                        value={getValue('maintenance_end_time')} 
                                        onChange={(e) => handleChange('maintenance_end_time', e.target.value)} 
                                        helperText="If set, displays a countdown timer on the maintenance page."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Switch checked={getValue('show_maintenance_countdown') === 'true'} onChange={(e) => handleChange('show_maintenance_countdown', e.target.checked)} color="primary" />}
                                            label="Show Countdown Timer to Public"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default SystemSettingsPage;
